// frontend/src/components/shared/LanguageSelector.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  normalizeLanguageCode,
} from '../../locales/languages';
import { saveLanguagePreference } from '../../utils/languageUtils';
import '../../styles/LanguageSelector.css';

interface LanguageItem {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
}

interface LanguageSelectorProps {
  compact?: boolean;
  showFlag?: boolean;
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  compact = false,
  showFlag = true,
  className = ''
}) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: LanguageItem[] = SUPPORTED_LANGUAGES as LanguageItem[];
  const currentLang: LanguageItem = getLanguageByCode(i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode: string) => {
    const liveLanguage = normalizeLanguageCode(langCode);
    if (!liveLanguage) return;

    try {
      await i18n.changeLanguage(liveLanguage);
      setIsOpen(false);
      await saveLanguagePreference(liveLanguage);
    } catch (error) {
      setIsOpen(false);
    }
  };

  return (
    <div className={`language-selector ${compact ? 'compact' : ''} ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`language-selector-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('changeLanguage') || 'Change Language'}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={t('changeLanguage') || 'Change Language'}
      >
        <span className="language-name">
          {currentLang.code.toUpperCase()}
        </span>
      </button>

      {isOpen && (
        <div className="language-dropdown" role="menu">
          <div className="language-dropdown-header">
            <span>{t('languageSelectorLabel') || 'Language'}</span>
          </div>
          <div className="language-list">
            {languages.map((lang) => (
              <button
                type="button"
                key={lang.code}
                className={`language-option ${lang.code === currentLang.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
                role="menuitemradio"
                aria-checked={lang.code === currentLang.code}
                lang={lang.code}
                title={lang.name}
              >
                {showFlag && lang.flag && <span className="language-flag">{lang.flag}</span>}
                <span className="language-native-name">{lang.nativeName}</span>
                <span className="language-english-name">({lang.name})</span>
                {lang.code === currentLang.code && (
                  <Check className="language-check-icon" size={16} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
